import { InventoriedInboundStockUpdateForm } from 'pages/Stocks/Inbound/InventoriedInboundStockForm';
import { roundToPrecision } from 'services/math';
import {
  CharacterizationForm,
  CharacterizationPost,
  ClientCreateForm,
  ClientPost,
  ContactPost,
  EstimatedReception,
  EstimatedReceptionForm,
  EstimatedReceptionPost,
  EstimatedReceptionPut,
  EstimatedReceptionSinglePut,
  Expedition,
  ExpeditionBuyerConfirmationForm,
  ExpeditionBuyerConfirmationPost,
  ExpeditionForm,
  ExpeditionPost,
  ExpeditionRepartitionAccepteeRepreneurBatch,
  ExpeditionRepartitionAccepteeRepreneurPost,
  ExpeditionRepartitionTonnageRepreneurFormData,
  InventoriedInboundStockPost,
  InventoriedOutboundStock,
  InventoriedOutboundStockBatch,
  InventoryFormInterface,
  Production,
  ProductionForm,
  ProductionPost,
  Reception,
  ReceptionForm,
  ReceptionPost,
  SingleProductionPost,
  SortingCenter,
  SortingCenterContract,
  SortingCenterContractEditFormType,
  SortingCenterContractFormType,
  SortingCenterContractPost,
  SortingCenterContractPut,
  SortingCenterContractsEditFormPut,
  SortingCenterContractsEditFormPutBatch,
  SortingCenterContractStatus,
} from 'types';
import {
  createUTCDate,
  createUTCDateFromDateString,
  formatDateAsQueryParam,
  getISOStringWithTimeZone,
  HIDDEN_TIME_UTC_HOURS,
  HIDDEN_TIME_UTC_MINUTES,
  HIDDEN_TIME_UTC_SECONDS,
} from 'utils/date';
import { floatWeightToInt } from 'utils/weight';
import { transformId } from '../services/apiPlatformTransformer';

export const formatExpeditionForEdit = (
  expedition: Expedition,
  sortingCenterContractStatus: { [key: number]: SortingCenterContractStatus } | null,
): ExpeditionForm => {
  const expeditionDateCurrentTZ = expedition.expeditionDate.split('+')[0];

  const expeditionDate = new Date(expeditionDateCurrentTZ);
  let expeditionTime: Date | null = new Date(expeditionDateCurrentTZ);

  if (
    expeditionTime.getHours() === HIDDEN_TIME_UTC_HOURS &&
    expeditionTime.getMinutes() === HIDDEN_TIME_UTC_MINUTES &&
    expeditionTime.getSeconds() === HIDDEN_TIME_UTC_SECONDS
  ) {
    expeditionTime = null;
  }

  return {
    formName: 'expeditionForm',
    material: expedition.material['@id'],
    buyer: expedition.buyer['@id'],
    conditioningQuantity: expedition.conditioningQuantity ?? '',
    weight: expedition.weight / 1000,
    expeditionDate,
    expeditionTime,
    truckIdentifier: expedition.truckIdentifier ?? '',
    expeditionClientPortions: expedition.expeditionClientPortions
      .sort((portionA, portionB) => {
        const statusOrder: Record<string, number> = {
          [SortingCenterContractStatus.CLOSED]: 1,
          [SortingCenterContractStatus.END_OF_PROCESSING]: 2,
          [SortingCenterContractStatus.ONGOING]: 3,
        };

        if (sortingCenterContractStatus) {
          const statusA = sortingCenterContractStatus[Number(transformId(portionA.client['@id']))];
          const statusB = sortingCenterContractStatus[Number(transformId(portionB.client['@id']))];
          if (statusA && statusB) {
            const statusDiff = statusOrder[statusA] - statusOrder[statusB];

            if (statusDiff === 0) {
              return portionA.client?.shortName.localeCompare(portionB.client?.shortName ?? '') ?? 0;
            }

            return statusDiff;
          }
        }

        return 0; // Return 0 if statuses or the status object are not available.
      })
      .map(({ availableStockWeight, weight, client, percentage }) => ({
        availableStockWeight: availableStockWeight ? availableStockWeight / 1000 : null,
        weight: String(weight / 1000),
        client: client['@id'],
        checked: weight > 0,
        percentage: null === percentage ? null : String(undefined === percentage ? 0 : percentage * 100),
      })),
  };
};

export const formatExpeditionBeforePut = (expedition: ExpeditionForm, sortingCenterId: string): ExpeditionPost => {
  return formatExpeditionBeforePost(expedition, sortingCenterId);
};

export const formatExpeditionBeforePost = (expedition: ExpeditionForm, sortingCenterId: string): ExpeditionPost => {
  const expeditionDate = expedition.expeditionDate ?? new Date();
  // If we DO NOT have a time set, use 00h00m00 as a magic time that should be hidden
  // when displayed back to the user.
  // Check method formatHourForDisplay in frontend/src/utils/date.ts
  const expeditionDatetime = new Date(
    expeditionDate.getFullYear(),
    expeditionDate.getMonth(),
    expeditionDate.getDate(),
    expedition.expeditionTime ? expedition.expeditionTime.getHours() : HIDDEN_TIME_UTC_HOURS,
    expedition.expeditionTime ? expedition.expeditionTime.getMinutes() : HIDDEN_TIME_UTC_MINUTES,
    expedition.expeditionTime &&
    expedition.expeditionTime.getHours() === 0 &&
    expedition.expeditionTime.getMinutes() === 0
      ? 1
      : HIDDEN_TIME_UTC_SECONDS,
  );

  // Fix ET-1387 : (\u0000 = nul) is imported from old e-tem database
  const truckIdentifier = -1 < ['', '\u0000'].indexOf(expedition.truckIdentifier) ? null : expedition.truckIdentifier;

  if (!Array.isArray(expedition.expeditionClientPortions)) {
    throw new Error('This should never happen');
  }
  const expeditionClientPortions = expedition.expeditionClientPortions.map(
    ({ weight, client, availableStockWeight, percentage }) => {
      return {
        availableStockWeight: floatWeightToInt(availableStockWeight ?? 0),
        weight: weight ? floatWeightToInt(parseFloat(weight ?? '0')) : 0,
        client,
        percentage: parseFloat(percentage ?? '0') / 100,
      };
    },
  );

  return {
    material: expedition.material,
    buyer: expedition.buyer,
    weight: floatWeightToInt(expedition.weight !== '' ? expedition.weight : 0),
    conditioningQuantity: expedition.conditioningQuantity || null,
    expeditionDate: getISOStringWithTimeZone(expeditionDatetime),
    truckIdentifier: truckIdentifier,
    sortingCenter: sortingCenterId,
    expeditionClientPortions,
  };
};

export const formatExpeditionBuyerConfirmation = (
  expeditionConfirmation: ExpeditionBuyerConfirmationForm,
  sortingCenterId: string,
  expeditionId: string,
): ExpeditionBuyerConfirmationPost => {
  const confirmationDate = expeditionConfirmation.confirmationDate ?? new Date();
  const confirmationDateTime = createUTCDate(
    confirmationDate.getFullYear(),
    confirmationDate.getMonth(),
    confirmationDate.getDate(),
    0,
    0,
    0,
  );
  return {
    decote: Number(expeditionConfirmation.decote) > 0 ? floatWeightToInt(Number(expeditionConfirmation.decote)) : null,
    confirmationDate: confirmationDateTime,
    weight: expeditionConfirmation.weight ? floatWeightToInt(expeditionConfirmation.weight) : null,
    sortingCenter: sortingCenterId,
    expedition: expeditionId,
  };
};

export const formatExpeditionRepartitionAccepteeRepreneurBatchPost = (
  expeditionRepartitionTonnageRepreneurFormData: ExpeditionRepartitionTonnageRepreneurFormData,
  sortingCenterId: string,
  expeditionDate: string,
): ExpeditionRepartitionAccepteeRepreneurBatch => {
  return {
    isMiseEnCoherence: expeditionRepartitionTonnageRepreneurFormData.isMiseEnCoherence,
    sortingCenter: sortingCenterId,
    expeditionDate,
    expeditionRepartitionAccepteeRepreneurBatch:
      expeditionRepartitionTonnageRepreneurFormData.expeditionRepartitionAccepteeRepreneur.map(
        expeditionRepartitionAccepteeRepreneur => {
          // Add roundToPrecision because in js 8.053 * 1000 = 8053,000000000001.
          const tonnageReprisInKilos = roundToPrecision(expeditionRepartitionAccepteeRepreneur.tonnageRepris * 1000);
          const objectFormatted: ExpeditionRepartitionAccepteeRepreneurPost = {
            sortingCenter: sortingCenterId,
            expeditionClientPortion: expeditionRepartitionAccepteeRepreneur.expeditionClientPortion,
            tonnageRepris: tonnageReprisInKilos,
            pourcentageRepris: expeditionRepartitionAccepteeRepreneur.pourcentageRepris,
          };
          if ('' !== expeditionRepartitionAccepteeRepreneur.id) {
            objectFormatted.id = expeditionRepartitionAccepteeRepreneur.id;
          }
          return objectFormatted;
        },
      ),
  };
};

export const formatReceptionBeforePost = (reception: ReceptionForm): ReceptionPost => {
  const receptionDateForm = reception.receptionDate ?? new Date();

  const receptionDatetime = new Date(
    receptionDateForm.getFullYear(),
    receptionDateForm.getMonth(),
    receptionDateForm.getDate(),
    reception.receptionTime!.getHours(),
    reception.receptionTime!.getMinutes(),
    0,
  );

  const grossWeight = reception.grossWeight ? floatWeightToInt(parseFloat(reception.grossWeight.toString())) : null;
  const tare = reception.tare ? Math.round(parseFloat(reception.tare.toString()) * 1000) : null;
  const proofOfWeightIdentifier = '' === reception.proofOfWeightIdentifier ? null : reception.proofOfWeightIdentifier;
  const incrementRejectStock = reception.refused && reception.incrementRejectStock;

  return {
    client: reception.client,
    stream: reception.stream,
    netWeight: floatWeightToInt(reception.netWeight ?? 0),
    grossWeight: grossWeight,
    tare: tare,
    refused: reception.refused,
    incrementRejectStock: incrementRejectStock,
    receptionDatetime: getISOStringWithTimeZone(receptionDatetime),
    proofOfWeightIdentifier: proofOfWeightIdentifier,
  };
};

export const formatEstimatedReceptionsBeforePut = (
  sortingCenterId: string,
  { estimatedReceptions, month, year, sortingCenter }: EstimatedReceptionForm,
): EstimatedReceptionPut => ({
  sortingCenter: sortingCenterId,
  estimatedReceptions: estimatedReceptions
    .filter(({ weight }) => !isNaN(weight))
    .map(({ id, client, stream, weight }) => ({
      id,
      client,
      stream,
      weight: floatWeightToInt(weight),
      month,
      year,
      sortingCenter,
    })),
});

export const formatReceptionForEdit = (reception: Reception): ReceptionForm => {
  const receptionDateCurrentTZ = reception.receptionDatetime.split('+')[0];

  const receptionDate = new Date(receptionDateCurrentTZ);
  let receptionTime: Date | null = new Date(receptionDateCurrentTZ);
  const incrementRejectStock = reception.refused && reception.incrementRejectStock;

  return {
    client: reception.client['@id'],
    stream: reception.stream['@id'],
    netWeight: reception.netWeight / 1000,
    grossWeight: reception.grossWeight ? reception.grossWeight / 1000 : '',
    tare: reception.grossWeight ? reception.tare / 1000 : '',
    refused: reception.refused,
    incrementRejectStock: incrementRejectStock,
    receptionDate,
    receptionTime,
    proofOfWeightIdentifier: reception.proofOfWeightIdentifier || '',
  };
};

export const formatCharacterizationBeforePost = (
  characterization: CharacterizationForm,
  sortingCenterId: string,
): CharacterizationPost => {
  const comment = '' === characterization.comment ? null : characterization.comment;
  const secteur = '' === characterization.secteur ? null : characterization.secteur;

  if (typeof characterization.characterizationMaterials === 'string') {
    throw new Error('This should never happen');
  }

  const characterizationMaterials = characterization.characterizationMaterials.map(characMaterial => ({
    weight: characMaterial.weight ? floatWeightToInt(characMaterial.weight) : 0,
    material: characMaterial.material,
    characterizationSubMaterials: characMaterial.characterizationSubMaterials?.map(characSubMaterial => ({
      weight: '' === characSubMaterial.weight ? null : floatWeightToInt(parseFloat(characSubMaterial.weight)),
      subMaterial: characSubMaterial.subMaterial,
      sortingCenter: sortingCenterId,
    })),
  }));

  const samplingDate = characterization.samplingDate ?? new Date();
  const samplingDatetime = createUTCDate(
    samplingDate.getFullYear(),
    samplingDate.getMonth(),
    samplingDate.getDate(),
    samplingDate.getHours(),
    samplingDate.getMinutes(),
    0,
  );

  const analyseDatetime = characterization.analyseDate
    ? createUTCDate(
        characterization.analyseDate.getFullYear(),
        characterization.analyseDate.getMonth(),
        characterization.analyseDate.getDate(),
        characterization.analyseDate.getHours(),
        characterization.analyseDate.getMinutes(),
        0,
      )
    : null;

  return {
    client: characterization.client,
    stream: characterization.stream,
    weight: floatWeightToInt(characterization.weight),
    sortingCenter: sortingCenterId,
    samplingDate: samplingDatetime,
    analyseDate: analyseDatetime,
    comment: comment,
    secteur: secteur,
    characterizationMaterials,
  };
};

export const formatClientBeforePost = (clientForm: ClientCreateForm, sortingCenter: SortingCenter): ClientPost => {
  const contact: ContactPost = {
    email: clientForm.contact.email ?? '',
    phoneNumber: clientForm.contact.phoneNumber ?? '',
    firstName: clientForm.contact.firstName ?? null,
    lastName: clientForm.contact.lastName ?? null,
  };

  return {
    shortName: clientForm.name,
    postalCode: clientForm.postalCode,
    city: clientForm.city,
    fullName: clientForm.fullName ?? '',
    addressLine: clientForm.addressLine ?? null,
    phoneNumber: clientForm.phoneNumber ?? null,
    inhabitants: parseInt(clientForm.inhabitants) ?? null,
    contact,
    sortingCenter: sortingCenter['@id'],
    code: clientForm.code,
  };
};

export const formatCharacterizationBeforePut = (
  characterization: CharacterizationForm,
  sortingCenterId: string,
): CharacterizationPost => {
  const comment = '' === characterization.comment ? null : characterization.comment;
  const secteur = '' === characterization.secteur ? null : characterization.secteur;

  if (typeof characterization.characterizationMaterials === 'string') {
    throw new Error('This should never happen');
  }

  const characterizationMaterials = characterization.characterizationMaterials.map(characMaterial => ({
    weight: characMaterial.weight ? floatWeightToInt(characMaterial.weight) : 0,
    material: characMaterial.material,
    characterizationSubMaterials: characMaterial.characterizationSubMaterials?.map(characSubMaterial => ({
      weight: '' === characSubMaterial.weight ? null : floatWeightToInt(parseFloat(characSubMaterial.weight)),
      subMaterial: characSubMaterial.subMaterial,
      sortingCenter: sortingCenterId,
    })),
  }));

  const samplingDate = characterization.samplingDate ?? new Date();
  const samplingDatetime = createUTCDate(
    samplingDate.getFullYear(),
    samplingDate.getMonth(),
    samplingDate.getDate(),
    samplingDate.getHours(),
    samplingDate.getMinutes(),
    0,
  );

  const analyseDatetime = characterization.analyseDate
    ? createUTCDate(
        characterization.analyseDate.getFullYear(),
        characterization.analyseDate.getMonth(),
        characterization.analyseDate.getDate(),
        characterization.analyseDate.getHours(),
        characterization.analyseDate.getMinutes(),
        0,
      )
    : null;

  return {
    client: characterization.client,
    stream: characterization.stream,
    weight: floatWeightToInt(characterization.weight),
    sortingCenter: sortingCenterId,
    samplingDate: samplingDatetime,
    analyseDate: analyseDatetime,
    comment: comment,
    secteur: secteur,
    characterizationMaterials,
  };
};

export const formatProductionBeforePost = (productionForm: ProductionForm, sortingCenterId: string): ProductionPost => {
  const productionDate = productionForm.productionDate ?? new Date();
  // If we DO NOT have a time set, use 00h00m00 as a magic time that should be hidden
  // when displayed back to the user.
  // Check method formatHourForDisplay in frontend/src/utils/date.ts
  const productionDatetime = createUTCDate(
    productionDate.getFullYear(),
    productionDate.getMonth(),
    productionDate.getDate(),
    productionForm.productionTime ? productionForm.productionTime.getHours() : HIDDEN_TIME_UTC_HOURS,
    productionForm.productionTime ? productionForm.productionTime.getMinutes() : HIDDEN_TIME_UTC_MINUTES,
    productionForm.productionTime ? 1 : HIDDEN_TIME_UTC_SECONDS,
  );

  return {
    sortingCenter: sortingCenterId,
    productions: productionForm.productionMaterials
      .filter(({ weight }) => weight > 0)
      .map(material => ({
        productionDate: productionDatetime,
        material: material.id,
        weight: floatWeightToInt(material.weight),
        conditioningQuantity: !!material.conditioningQuantity ? material.conditioningQuantity : undefined,
        sortingCenter: sortingCenterId,
      })),
  };
};

export const formatInventoriedInboundStocksBeforePost = (
  inventoriedInboundStockForm: InventoriedInboundStockUpdateForm,
  sortingCenterId: string,
): InventoriedInboundStockPost[] => {
  return inventoriedInboundStockForm.stocks.map(stock => {
    let separationDate = null;

    if (stock.separationDate) {
      separationDate = new Date(stock.separationDate);

      if (stock.separationHour) {
        const separationHour = new Date(stock.separationHour);
        separationDate.setHours(separationHour.getHours(), separationHour.getMinutes());
      }

      separationDate = formatDateAsQueryParam(separationDate, true);
    }
    return {
      id: stock.id,
      month: Number(stock.month),
      year: Number(stock.year),
      weight: stock.weight ? Number(stock.weight) * 1000 : 0,
      stream: stock.stream['@id'],
      separationDate,
      sortingCenter: sortingCenterId,
    };
  });
};

export const formatSortingCenterContractsBeforePut = (
  sortingCenterContractsForm: SortingCenterContractsEditFormPutBatch,
): SortingCenterContractsEditFormPut[] => {
  return sortingCenterContractsForm.activeContracts.map((activeContract: SortingCenterContractsEditFormPut) => ({
    id: activeContract.id,
    characterizationNumber: activeContract.characterizationNumber,
    endDate: formatDateAsQueryParam(new Date(activeContract.endDate)),
  }));
};

export const formatInventoriedOutboundStockBeforePost = (
  inventoryForm: InventoryFormInterface | null,
  sortingCenter: string,
) => {
  const inventoriedOutboundStockFormatted: InventoriedOutboundStockBatch = {
    sortingCenter,
    inventoriedOutboundStocks: [],
  };

  if (inventoryForm) {
    inventoriedOutboundStockFormatted.inventoriedOutboundStocks = inventoryForm.conditionedStocks.map(
      conditionedStock => {
        const inventoryMonth =
          typeof conditionedStock.inventoryMonth === 'number'
            ? conditionedStock.inventoryMonth
            : parseInt(conditionedStock.inventoryMonth);

        const inventoryYear =
          typeof conditionedStock.inventoryYear === 'number'
            ? conditionedStock.inventoryYear
            : parseInt(conditionedStock.inventoryYear);

        const inventoryWeight =
          typeof conditionedStock.inventoryWeight === 'number'
            ? conditionedStock.inventoryWeight
            : parseFloat(conditionedStock.inventoryWeight);

        const inventoryAverageBallWeight =
          typeof conditionedStock.inventoryAverageBallWeight === 'number'
            ? conditionedStock.inventoryAverageBallWeight
            : parseFloat(conditionedStock.inventoryAverageBallWeight ?? 0);

        const calculatedAverageBallWeightInKilos =
          typeof conditionedStock.calculatedAverageBallWeightInKilos === 'number'
            ? conditionedStock.calculatedAverageBallWeightInKilos
            : parseFloat(conditionedStock.calculatedAverageBallWeightInKilos ?? 0);

        const inventoryConditioningQuantity =
          typeof conditionedStock.inventoryConditioningQuantity === 'number'
            ? conditionedStock.inventoryConditioningQuantity
            : parseInt(conditionedStock.inventoryConditioningQuantity);

        const currentInventoriedOutboundStockFormatted: InventoriedOutboundStock = {
          month: inventoryMonth,
          year: inventoryYear,
          weight: Math.round(inventoryWeight * 1000),
          averageWeight: Math.round(inventoryAverageBallWeight * 1000),
          calculatedAverageBallWeight: Math.round(calculatedAverageBallWeightInKilos),
          conditioningQuantity: inventoryConditioningQuantity,
          material: conditionedStock.material,
          sortingCenter: conditionedStock.sortingCenter,
        };

        if (conditionedStock.inventoryIdApiPlatform) {
          currentInventoriedOutboundStockFormatted.id = conditionedStock.inventoryIdApiPlatform;
        }

        return currentInventoriedOutboundStockFormatted;
      },
    );
  }

  return inventoriedOutboundStockFormatted;
};

export const formatProductionBeforePut = (
  productionForm: ProductionForm,
  sortingCenterId: string,
): SingleProductionPost => {
  const productionDate = productionForm.productionDate ?? new Date();
  // If we DO NOT have a time set, use 00h00m00 as a magic time that should be hidden
  // when displayed back to the user.
  // Check method formatHourForDisplay in frontend/src/utils/date.ts
  const productionDatetime = createUTCDate(
    productionDate.getFullYear(),
    productionDate.getMonth(),
    productionDate.getDate(),
    productionForm.productionTime ? productionForm.productionTime.getHours() : HIDDEN_TIME_UTC_HOURS,
    productionForm.productionTime ? productionForm.productionTime.getMinutes() : HIDDEN_TIME_UTC_MINUTES,
    productionForm.productionTime ? 1 : HIDDEN_TIME_UTC_SECONDS,
  );

  const material = productionForm.productionMaterials[0];

  return {
    productionDate: productionDatetime,
    material: material.id,
    weight: floatWeightToInt(material.weight),
    conditioningQuantity: !!material.conditioningQuantity ? material.conditioningQuantity : null,
    sortingCenter: sortingCenterId,
  };
};

export const formatProductionForEdit = (production: Production): ProductionForm => {
  const productionDateCurrentTZ = production.productionDate.split('+')[0];

  const productionDate = new Date(productionDateCurrentTZ);
  let productionTime: Date | null = new Date(productionDateCurrentTZ);

  if (
    productionTime.getHours() === HIDDEN_TIME_UTC_HOURS &&
    productionTime.getMinutes() === HIDDEN_TIME_UTC_MINUTES &&
    productionTime.getSeconds() === HIDDEN_TIME_UTC_SECONDS
  ) {
    productionTime = null;
  }

  return {
    productionDate,
    productionTime,
    productionMaterials: [
      {
        conditioningQuantity: production.conditioningQuantity ?? 0,
        id: production.material['@id'],
        weight: production.weight / 1000,
      },
    ],
  };
};

export const formatSortingCenterContractBeforePost = (
  sortingCenterContractForm: SortingCenterContractFormType,
  sortingCenterId: string,
): SortingCenterContractPost => {
  const startDate = sortingCenterContractForm.startDate ?? new Date();
  const endDate = sortingCenterContractForm.endDate ?? new Date();
  const characterizationNumber = sortingCenterContractForm.characterizationNumber
    ? parseInt(sortingCenterContractForm.characterizationNumber)
    : 0;

  return {
    client: sortingCenterContractForm.client,
    stream: sortingCenterContractForm.stream,
    characterizationNumber: characterizationNumber,
    startDate: createUTCDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0),
    endDate: createUTCDate(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0),
    sortingCenter: sortingCenterId,
  };
};

export const formatSortingCenterContractBeforePut = (
  sortingCenterContractForm: SortingCenterContractEditFormType,
  sortingCenterId: string,
): SortingCenterContractPut => {
  const startDate = sortingCenterContractForm.startDate;
  const endDate = sortingCenterContractForm.endDate;
  const characterizationNumber = sortingCenterContractForm.characterizationNumber
    ? parseInt(sortingCenterContractForm.characterizationNumber, 10)
    : 0;

  return {
    '@id': sortingCenterContractForm['@id'],
    id: sortingCenterContractForm.id,
    client: sortingCenterContractForm.client,
    stream: sortingCenterContractForm.stream,
    characterizationNumber: characterizationNumber,
    startDate: createUTCDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0),
    endDate: createUTCDate(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0),
    sortingCenter: sortingCenterId,
  };
};

export const formatEstimatedReceptionBeforePost = (
  sortingCenterContractForm: SortingCenterContractFormType,
  sortingCenterId: string,
): EstimatedReceptionPost => {
  const startDate = sortingCenterContractForm.startDate
    ? createUTCDate(
        sortingCenterContractForm.startDate.getFullYear(),
        sortingCenterContractForm.startDate.getMonth(),
        sortingCenterContractForm.startDate.getDate(),
        0,
        0,
        0,
      )
    : new Date();

  const weight = sortingCenterContractForm.estimatedReceptionWeight
    ? floatWeightToInt(parseFloat(sortingCenterContractForm.estimatedReceptionWeight))
    : 0;

  return {
    client: sortingCenterContractForm.client,
    stream: sortingCenterContractForm.stream,
    sortingCenter: sortingCenterId,
    year: startDate.getFullYear(),
    month: startDate.getMonth() + 1,
    weight: weight,
  };
};

export const formatEstimatedReceptionBeforePut = (
  sortingCenterContract: SortingCenterContractEditFormType,
  sortingCenterId: string,
): EstimatedReceptionSinglePut => {
  const estimatedReceptionId = sortingCenterContract.estimatedReception['@id'];
  const weight = sortingCenterContract.estimatedReceptionWeight
    ? floatWeightToInt(parseFloat(sortingCenterContract.estimatedReceptionWeight))
    : 0;
  return {
    id: estimatedReceptionId,
    client: sortingCenterContract.client,
    stream: sortingCenterContract.stream,
    sortingCenter: sortingCenterId,
    month: sortingCenterContract.startDate.getMonth() + 1,
    year: sortingCenterContract.startDate.getFullYear(),
    weight: weight,
  };
};

export const formatSortingCenterContractForEdit = (
  sortingCenterContract: SortingCenterContract,
  estimatedReception: EstimatedReception,
): SortingCenterContractEditFormType => {
  const estimatedWeight = estimatedReception.weight / 1000;
  const result = {
    '@id': sortingCenterContract['@id'],
    id: sortingCenterContract.id,
    client: sortingCenterContract.client['@id'],
    stream: sortingCenterContract.stream['@id'],
    streamChoice: sortingCenterContract.stream,
    characterizationNumber: String(sortingCenterContract.characterizationNumber),
    startDate: new Date(sortingCenterContract.startDate),
    endDate: createUTCDateFromDateString(sortingCenterContract.endDate),
    estimatedReceptionWeight: String(estimatedWeight),
    estimatedReception: estimatedReception,
    sortingCenter: sortingCenterContract.sortingCenter['@id'],
  };
  return result;
};
